import React, { useEffect, useRef, useState } from "react";
import { Quote } from "lucide-react";
import { SITE } from "../lib/site";

export const hasPortrait = () => !!SITE.portrait?.trim();

function useReveal(threshold = 0.35) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

const reveal = (visible, delay = "") =>
  `transition-[opacity,transform] duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
  } ${delay}`;

export function QuoteCard() {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      data-testid="intro-quote-card"
      data-visible={visible}
      className={`rounded-2xl bg-sand-200/40 border border-sand-300/60 p-8 max-w-sm mx-auto md:mx-0 ${reveal(visible)}`}
    >
      <Quote size={28} className={`text-sand-500 mb-5 ${reveal(visible, "delay-150")}`} />
      <p className={`font-serif italic text-sand-900 text-2xl leading-snug ${reveal(visible, "delay-300")}`}>
        « {SITE.baseline} »
      </p>
      <p className={`mt-6 eyebrow text-sand-600 ${reveal(visible, "delay-500")}`}>{SITE.practitioner}</p>
    </div>
  );
}

export function Portrait({ withQuote = false, alt }) {
  const [ref, visible] = useReveal(0.25);
  return (
    <div ref={ref} data-testid="portrait-slot" className={`relative max-w-sm mx-auto md:mx-0 ${reveal(visible)}`}>
      <img
        src={SITE.portrait}
        alt={alt || `${SITE.practitioner}, praticienne en hypnose régressive et spirituelle à Pantin`}
        loading="lazy"
        className="rounded-2xl object-cover w-full aspect-[4/5] shadow-xl"
      />
      {withQuote && (
        <div className={`hidden md:block absolute -bottom-6 -right-6 glass rounded-2xl p-5 max-w-[220px] ${reveal(visible, "delay-300")}`}>
          <p className="font-serif italic text-sand-900 text-lg leading-snug">« {SITE.baseline} »</p>
        </div>
      )}
    </div>
  );
}

export default function PortraitSlot({ alt }) {
  return hasPortrait() ? <Portrait withQuote alt={alt} /> : <QuoteCard />;
}
